// VulnerabilityTable
// ------------------
// Displays the list of vulnerabilities for the selected test.
// Toolbar: select-all, bulk action, sort.
// Table: Tailwind-styled rows via SingleVulnRow.
// Pagination: inline, matching the /applications and /users pattern.

import React, { useState, useEffect, useCallback } from "react";
import { ChevronLeft, ChevronRight, Loader2 } from "lucide-react";
import { generatePath, useLocation, useNavigate } from "react-router-dom";
import VulnerabilityOperationsClass from "@/models/vulnerabilityOperations";
import {
  eventBus,
  LIVE_FINDINGS_UPDATE,
  NEW_TEST,
  VULN_STATUS_UPDATE,
  VULN_UPDATE,
} from "../../services/eventBus.service";
import SingleVulnRow from "./SingleVulnRow";
import VulnerabilityClass from "@/models/vulnerability";
import WebApplicationClass from "@/models/webApplication";
import { isWebAppTestRunning } from "@/utils/liveTestState";
import SortSelect from "@/components/common/SortSelect";
import {
  EMPTY_STRING,
  IN_FLIGHT_TEST_STATUSES,
  RISK_RATING_CRITICAL,
  RISK_RATING_HIGH,
  RISK_RATING_INFO,
  RISK_RATING_LOW,
  RISK_RATING_MED,
  VULNERABILITY_PAGE,
} from "../../constants/definitions";
import TestResultsClass from "@/models/testResult";
import { getSeverityFilterActiveClass } from "@/utils/severityStyles";

const SORT_VULNS_BY_ALPHA = "Reference";
const SORT_VULNS_BY_VULN = "Vulnerability";
const SORT_VULNS_BY_HIGHEST_RISK = "Highest Risk";
const SORT_VULNS_BY_LOWEST_RISK = "Lowest Risk";

// value === label here — the existing sortVulns dispatcher keys off
// the human-readable string. Kept simple because the values aren't
// surfaced anywhere else.
//
// Sort-by-Status was retired in the v1 vuln-state-model rework — every
// vuln in the table is now "Active" (observed in the latest test), so
// sorting by status no longer produces a meaningful ordering.
const SORT_OPTIONS: { value: string; label: string }[] = [
  { value: SORT_VULNS_BY_HIGHEST_RISK, label: SORT_VULNS_BY_HIGHEST_RISK },
  { value: SORT_VULNS_BY_LOWEST_RISK,  label: SORT_VULNS_BY_LOWEST_RISK },
  { value: SORT_VULNS_BY_ALPHA,        label: SORT_VULNS_BY_ALPHA },
  { value: SORT_VULNS_BY_VULN,         label: SORT_VULNS_BY_VULN },
];

// Default ordering applied to vulns when they're hydrated (mirrors
// setFullTestResult's sortHighestRisk). The select reflects this so
// the user can see the current order at a glance.
const DEFAULT_SORT = SORT_VULNS_BY_HIGHEST_RISK;

const MAX_VULNS_PER_PAGE = 10;

interface VulnerabilityTableProps {
  webapp: WebApplicationClass;
  testSelected: TestResultsClass;
  testId: string;
  // Severity filter — null = All. Set by either the TestDetails
  // tiles above the table or the in-header filter pills.
  severityFilter: string | null;
  onSeverityFilterChange: (filter: string | null) => void;
}

function getVulnsFromProps(testSelected: TestResultsClass): VulnerabilityClass[] {
  // getDisplayVulns() is the single source of truth for "what has this test
  // found". For a terminal test that's the hydrated backend records; during
  // an in-flight run it's those records — the backend persists each finding
  // as it's made — unioned with any slim rows the discovery subscription has
  // streamed since the last fetch.
  //
  // This used to read liveFindings EXCLUSIVELY while a test was in flight,
  // which meant the table was backed by session memory alone: a refresh or a
  // re-login mid-run wiped every finding off the surface even though the
  // backend had them all along.
  //
  // Slim rows carry no detail blob, but they remain clickable — the detail
  // page pulls the full vuln on demand (Vulnerability.loadVulnFromUrl →
  // getFullTestResult) before rendering.
  return testSelected?.getDisplayVulns() ?? [];
}

function VulnerabilityTable({
  webapp,
  testSelected,
  testId,
  severityFilter,
  onSeverityFilterChange,
}: VulnerabilityTableProps) {
  const location = useLocation();
  const navigate = useNavigate();

  const [currentPaginationPosition, setCurrentPaginationPosition] = useState(0);
  const [sortKey, setSortKey] = useState<string>(DEFAULT_SORT);
  // Bumped by the event bus to force a re-read of the model below. The
  // findings themselves are NOT held in state — see the derivation.
  const [, bumpFindingsRender] = useState(0);

  // The rows this table renders, derived from the model on every render
  // rather than copied into state.
  //
  // The copy was a correctness bug, not just indirection. Its resync effect
  // fired on [testSelected, testId, testSelected.vulns] — an array IDENTITY
  // check — so any change that didn't reassign `vulns` left the table
  // rendering a snapshot the rest of the page had moved on from. That is
  // exactly what happened when a test ended and its final-result fetch
  // failed: the tiles above recomputed from the model and read 0, the table
  // still held the rows it had copied mid-run, and the two surfaces stated
  // different numbers for the same test. Deriving means they cannot
  // disagree, whatever the model does next.
  const vulns = getVulnsFromProps(testSelected);

  // filteredVulns is the view the table actually paginates over.
  // Sort, action and selection logic references this filtered list
  // so indices line up with what the user sees. severityCounts pill
  // values come from the unfiltered `vulns` so the user always sees
  // the full distribution regardless of the active filter.
  //
  // The sort step is applied here (rather than only inside the
  // sortVulns callback that fires on dropdown change) so that the
  // displayed order is always whatever the dropdown says it is.
  // Previously: if testSelected.vulns arrived in any order other
  // than the upstream's sortHighestRisk output — e.g. straight from
  // the dashboard preload before getFullTestResult landed, or after
  // a live-vuln subscription appended — the dropdown could claim
  // "Highest Risk" while the rendered rows weren't actually sorted.
  //
  // Not memoised: `vulns` is derived fresh each render, so a memo keyed on
  // it would never hit — and one that appeared to would be worse, because
  // findings mutate in place and a cached ordering could outlive the data.
  // Both passes are O(findings) over at most a few hundred rows.
  const filteredVulns = ((): VulnerabilityClass[] => {
    const vulnOps = new VulnerabilityOperationsClass();
    let ordered: VulnerabilityClass[];
    if (sortKey === SORT_VULNS_BY_HIGHEST_RISK) {
      ordered = vulnOps.sortHighestRisk(vulns);
    } else if (sortKey === SORT_VULNS_BY_LOWEST_RISK) {
      ordered = vulnOps.sortLowestRisk(vulns);
    } else if (sortKey === SORT_VULNS_BY_ALPHA) {
      ordered = vulnOps.sortReference(vulns);
    } else if (sortKey === SORT_VULNS_BY_VULN) {
      ordered = vulnOps.sortVulnerabilityType(vulns);
    } else {
      ordered = vulns;
    }
    if (!severityFilter) return ordered;
    return ordered.filter((vuln) => vuln.riskRating === severityFilter);
  })();

  // Pill counts come from the unfiltered set so the user always sees the
  // full distribution, whatever filter is active.
  const severityCounts = ((): {
    crit: number;
    high: number;
    med: number;
    low: number;
    info: number;
    total: number;
  } => {
    let crit = 0;
    let high = 0;
    let med = 0;
    let low = 0;
    let info = 0;
    for (const vuln of vulns) {
      if (vuln.riskRating === RISK_RATING_CRITICAL) crit++;
      else if (vuln.riskRating === RISK_RATING_HIGH) high++;
      else if (vuln.riskRating === RISK_RATING_MED) med++;
      else if (vuln.riskRating === RISK_RATING_LOW) low++;
      else if (vuln.riskRating === RISK_RATING_INFO) info++;
    }
    return { crit, high, med, low, info, total: crit + high + med + low + info };
  })();

  // Start at page 1 whenever the test being viewed changes — page 4 of the
  // previous test's findings is meaningless against a different test, and
  // may not even exist.
  useEffect(() => {
    setCurrentPaginationPosition(0);
  }, [testSelected?.id, testId]);

  // Re-render on model mutations React cannot see. Findings arrive by being
  // pushed onto arrays in place (the discovery stream appends to
  // liveFindings; a status flip mutates a vuln), so nothing about the props
  // changes and no re-render would otherwise happen. These events are the
  // signal that the model moved; the derivation above does the reading.
  //
  // No dependency on testSelected: the handler reads nothing from the
  // closure, so it can't go stale and the listeners don't churn as the
  // page promotes its selection during mount.
  useEffect(() => {
    const handleModelChanged = () => bumpFindingsRender((count) => count + 1);

    eventBus.on(VULN_UPDATE, handleModelChanged);
    eventBus.on(VULN_STATUS_UPDATE, handleModelChanged);
    eventBus.on(NEW_TEST, handleModelChanged);
    eventBus.on(LIVE_FINDINGS_UPDATE, handleModelChanged);

    return () => {
      eventBus.off(VULN_UPDATE, handleModelChanged);
      eventBus.off(VULN_STATUS_UPDATE, handleModelChanged);
      eventBus.off(NEW_TEST, handleModelChanged);
      eventBus.off(LIVE_FINDINGS_UPDATE, handleModelChanged);
    };
  }, []);

  // ---- Sort ----

  // Dropdown change handler — just records the new sortKey. The
  // filteredVulns memo above does the actual ordering, so this stays
  // a one-liner. Pagination resets so the user lands on the first
  // page of the freshly-ordered list rather than possibly mid-way
  // through the previous ordering.
  const sortVulns = useCallback(
    (sortMethod: string) => {
      setSortKey(sortMethod);
      setCurrentPaginationPosition(0);
    },
    []
  );

  // ---- Pagination ----

  const selectedPageCallback = useCallback((pageNumber: number) => {
    setCurrentPaginationPosition(pageNumber);
  }, []);

  // ---- Navigation ----

  const openSingleVulnPage = useCallback(
    (newPosition: number) => {
      const vuln = filteredVulns[newPosition];
      if (!vuln) return;
      const searchParams = new URLSearchParams(location.search);
      searchParams.set("vulnid", vuln.id);
      navigate(
        `${generatePath(VULNERABILITY_PAGE, {
          webappid: webapp.id,
          testid: testId,
        })}?${searchParams.toString()}`
      );
    },
    [location.search, filteredVulns, navigate, webapp.id, testId]
  );

  // ---- Render helpers ----

  const ListVulnerabilities = useCallback(() => {
    if (!filteredVulns.length) return null;

    const sliceStart = currentPaginationPosition * MAX_VULNS_PER_PAGE;
    const pageVulns = filteredVulns.slice(
      sliceStart,
      sliceStart + MAX_VULNS_PER_PAGE
    );

    return pageVulns.map((vuln, pageIndex) => {
      const globalIndex = sliceStart + pageIndex;
      return (
        <SingleVulnRow
          key={vuln.id || globalIndex}
          vuln={vuln}
          position={globalIndex}
          onOpenVuln={openSingleVulnPage}
        />
      );
    });
  }, [
    filteredVulns,
    currentPaginationPosition,
    openSingleVulnPage,
  ]);

  // Reset pagination whenever the filter changes so the user lands
  // on page 1 of the filtered view.
  useEffect(() => {
    setCurrentPaginationPosition(0);
  }, [severityFilter]);

  const totalVulns = filteredVulns.length;
  const totalPages = Math.ceil(totalVulns / MAX_VULNS_PER_PAGE);
  const startIndex = currentPaginationPosition * MAX_VULNS_PER_PAGE;

  // Filter pills config — mirrors the dashboard's Open Vulnerabilities
  // table. Counts always come from the full vulns set so the user can
  // see the total distribution; severities with zero vulns are hidden
  // (except "All", which is always shown).
  const filterPills: {
    label: string;
    value: string | null;
    count: number;
    activeStyle: string;
  }[] = [
    { label: "All",      value: null,                 count: severityCounts.total, activeStyle: "bg-gray-900 text-white" },
    { label: "Critical", value: RISK_RATING_CRITICAL, count: severityCounts.crit,  activeStyle: getSeverityFilterActiveClass(RISK_RATING_CRITICAL) },
    { label: "High",     value: RISK_RATING_HIGH,     count: severityCounts.high,  activeStyle: getSeverityFilterActiveClass(RISK_RATING_HIGH) },
    { label: "Medium",   value: RISK_RATING_MED,      count: severityCounts.med,   activeStyle: getSeverityFilterActiveClass(RISK_RATING_MED) },
    { label: "Low",      value: RISK_RATING_LOW,      count: severityCounts.low,   activeStyle: getSeverityFilterActiveClass(RISK_RATING_LOW) },
    { label: "Info",     value: RISK_RATING_INFO,     count: severityCounts.info,  activeStyle: getSeverityFilterActiveClass(RISK_RATING_INFO) },
  ];

  return (
    <div>
      {/* Card header — title on the left, severity filter pills + sort
          select on the right. Mirrors the dashboard's Open Vulnerabilities
          layout. flex-wrap so the right group falls onto its own line on
          narrow screens rather than overflowing. Generous vertical
          padding and a larger heading give the title breathing room
          from the filter pills, which would otherwise crowd it. */}
      <div className="px-6 py-6 border-b border-gray-100 flex items-center justify-between gap-8 flex-wrap">
        <div className="flex items-center gap-3">
          <h2 className="text-lg font-semibold text-gray-900">Vulnerabilities</h2>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          {severityCounts.total > 0 && (
            <div className="flex items-center gap-1.5">
              {filterPills.map((pill) => {
                if (pill.count === 0 && pill.value !== null) return null;
                const isActive = severityFilter === pill.value;
                return (
                  <button
                    key={pill.label}
                    type="button"
                    onClick={() =>
                      onSeverityFilterChange(
                        isActive && pill.value !== null ? null : pill.value
                      )
                    }
                    className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium transition-colors focus:outline-none ${
                      isActive
                        ? pill.activeStyle
                        : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                    }`}
                  >
                    {pill.label}
                    <span className={`tabular-nums ${isActive ? "opacity-80" : "text-gray-400"}`}>
                      {pill.count}
                    </span>
                  </button>
                );
              })}
            </div>
          )}
          <SortSelect<string>
            id="vuln-sort"
            value={sortKey}
            onChange={sortVulns}
            options={SORT_OPTIONS}
            ariaLabel="Sort vulnerabilities"
          />
        </div>
      </div>

      {/* Table */}
      {totalVulns === 0 ? (
        <div className="text-center py-12">
          {(testSelected?.testStatus &&
            IN_FLIGHT_TEST_STATUSES.has(testSelected.testStatus)) ||
          // Deferred-selection case: the live-run signal says a test is
          // running but the in-flight entry isn't in webapp.tests yet, so
          // testSelected is undefined for the brief backfill window. Treat
          // as "in-progress" so the surface doesn't flash "No
          // vulnerabilities found" before the dropdown auto-selects
          // the live entry.
          (!testSelected && isWebAppTestRunning(webapp)) ? (
            <>
              <p className="text-sm font-medium text-gray-700">Test in progress</p>
              <p className="text-xs text-gray-500 mt-1">
                Vulnerabilities are reported as they're found.
              </p>
            </>
          ) : testSelected && !testSelected.fullResultLoaded ? (
            // Stub-selection case: the history backfill seeds tests
            // slim; the user has picked a historical test from the
            // dropdown and getFullTestResult is in flight. Show a
            // loading state so an empty stub array doesn't read as
            // "No vulnerabilities found" while the fetch is pending.
            <div
              role="status"
              aria-live="polite"
              className="flex items-center justify-center gap-2 text-sm text-gray-500"
            >
              <Loader2
                className="h-4 w-4 animate-spin motion-reduce:animate-none"
                aria-hidden="true"
              />
              <span>Loading vulnerabilities…</span>
            </div>
          ) : !testSelected ? (
            // Never-run case: no terminal test exists to report on (the
            // same condition that disables the Report button). Point the
            // user at the action rather than implying a clean result.
            <p className="text-sm text-gray-500">
              Run your first pentest to see vulnerabilities here.
            </p>
          ) : (
            // A terminal test is selected and genuinely produced no
            // findings — a real, clean result.
            <p className="text-sm text-gray-500">
              No vulnerabilities found
            </p>
          )}
        </div>
      ) : (
        <div className="overflow-x-auto">
          {/* table-fixed locks the column widths so they don't reflow as
              row content changes (e.g. when a status flips from "Open"
              to "Risk Accepted", or vuln names of different lengths
              hydrate in). Widths set on the <th> cells become the
              canonical column widths; the Vulnerability column has no
              explicit width so it absorbs whatever space is left. */}
          <table className="w-full text-sm table-fixed">
            {/* Column order + headings mirror the Dashboard's Open
                Vulnerabilities table (Risk → Reference → Vulnerability →
                First Found → Last Seen) minus the Application column,
                which is implied on this page. Keeps the two surfaces
                consistent so users aren't re-orienting when they move
                between them. */}
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50/50">
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-24">
                  Risk
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap w-28">
                  Reference
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Vulnerability
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap w-32">
                  First Found
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap w-32">
                  Last Seen
                </th>
              </tr>
            </thead>
            <tbody>{ListVulnerabilities()}</tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between px-6 py-3 border-t border-gray-100">
          <p className="text-sm text-gray-500">
            Showing {startIndex + 1}–
            {Math.min(startIndex + MAX_VULNS_PER_PAGE, totalVulns)} of {totalVulns}
          </p>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => selectedPageCallback(currentPaginationPosition - 1)}
              disabled={currentPaginationPosition === 0}
              className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600 disabled:opacity-30 disabled:hover:bg-transparent"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            {Array.from({ length: totalPages }, (_, pageNumber) => (
              <button
                key={pageNumber}
                type="button"
                onClick={() => selectedPageCallback(pageNumber)}
                className={[
                  "rounded-lg px-3 py-1 text-sm transition-colors",
                  currentPaginationPosition === pageNumber
                    ? "bg-blue-600 text-white"
                    : "text-gray-500 hover:bg-gray-100",
                ].join(" ")}
              >
                {pageNumber + 1}
              </button>
            ))}
            <button
              type="button"
              onClick={() => selectedPageCallback(currentPaginationPosition + 1)}
              disabled={currentPaginationPosition === totalPages - 1}
              className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600 disabled:opacity-30 disabled:hover:bg-transparent"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default VulnerabilityTable;
